import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import useBranches from "@/hooks/useBranches";
import { DownloadSimple, Bank } from "@phosphor-icons/react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function downloadCsv(filename, headers, rows) {
  const csv = [headers, ...rows]
    .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function HrPfEsiReportPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || (user?.permissions || []).includes("branches.view_all");
  const { branches } = useBranches({ activeOnly: true });
  const [month, setMonth] = useState(currentMonth());
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!month) return;
    setLoading(true);
    try {
      const params = { month };
      if (branchId) params.branch_id = branchId;
      const { data } = await api.get("/payroll/pf-esi-report", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [month, branchId]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const t = data?.totals || {};

  const exportSimple = () => {
    if (!rows.length) { toast.error("Nothing to export"); return; }
    downloadCsv(
      `pf-esi-simple-${month}.csv`,
      ["month", "employee_code", "name", "branch", "gross", "pf_employee", "pf_employer", "esi_employee", "esi_employer", "total_deduction"],
      [
        ...rows.map((r) => [
          month, r.employee_code, r.name, r.branch_name, r.gross,
          r.epf_ee, r.eps_er + r.epf_er, r.esi_ee, r.esi_er, r.total_ee,
        ]),
        ["TOTAL", "", "", "", t.gross, t.epf_ee, (t.eps_er || 0) + (t.epf_er || 0), t.esi_ee, t.esi_er, ""],
      ],
    );
  };

  const exportStatutory = () => {
    if (!rows.length) { toast.error("Nothing to export"); return; }
    downloadCsv(
      `pf-esi-statutory-${month}.csv`,
      [
        "month", "employee_code", "name", "branch",
        "gross_wages", "epf_wages", "epf_employee_12pct", "eps_employer_8_33pct", "epf_employer_3_67pct",
        "esi_wages", "esi_employee_0_75pct", "esi_employer_3_25pct",
        "total_employee_share", "total_employer_share",
      ],
      [
        ...rows.map((r) => [
          month, r.employee_code, r.name, r.branch_name,
          r.gross, r.epf_wages, r.epf_ee, r.eps_er, r.epf_er,
          r.esi_wages, r.esi_ee, r.esi_er,
          r.total_ee, r.total_er,
        ]),
        [
          "TOTAL", "", "", "",
          t.gross, t.epf_wages, t.epf_ee, t.eps_er, t.epf_er,
          t.esi_wages, t.esi_ee, t.esi_er,
          (t.epf_ee || 0) + (t.esi_ee || 0), (t.eps_er || 0) + (t.epf_er || 0) + (t.esi_er || 0),
        ],
      ],
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="HR / Reports"
        title="PF / ESI Monthly Report"
        subtitle={
          data
            ? `EPF wage ceiling ${inr(data.epf_ceiling)} · ESI ceiling ${inr(data.esi_ceiling)} — month ${data.month}`
            : "Employee & employer PF / ESI contributions per month"
        }
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={exportSimple}
              data-testid="export-simple-csv"
              className="px-3 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink flex items-center gap-1.5"
            >
              <DownloadSimple size={12} /> CSV · Simple
            </button>
            <button
              onClick={exportStatutory}
              data-testid="export-statutory-csv"
              className="px-3 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-brand flex items-center gap-1.5"
            >
              <DownloadSimple size={12} /> CSV · Statutory
            </button>
          </div>
        }
      />

      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">Month</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="report-month"
            className="px-3 py-2 bg-canvas border border-canvas-line text-sm"
          />
        </div>
        {isAdmin && (
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">Branch</label>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              data-testid="report-branch"
              className="px-3 py-2 bg-canvas border border-canvas-line text-sm"
            >
              <option value="">All branches</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          ["Gross Wages", t.gross],
          ["PF (Employee + Employer)", (t.epf_ee || 0) + (t.eps_er || 0) + (t.epf_er || 0)],
          ["ESI (Employee + Employer)", (t.esi_ee || 0) + (t.esi_er || 0)],
          ["Employees", rows.length],
        ].map(([label, val]) => (
          <div key={label} className="border border-canvas-line p-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">{label}</div>
            <div className="font-mono tabular-nums text-xl font-semibold mt-1" data-testid={`kpi-${String(label).toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {typeof val === "number" && label !== "Employees" ? inr(val) : val ?? "—"}
            </div>
          </div>
        ))}
      </div>

      <div className="border border-canvas-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line">
            <tr>
              {["Code", "Name", "Branch", "Gross ₹", "PF (EE)", "EPS (ER)", "EPF (ER)", "ESI (EE)", "ESI (ER)", "Total Share"].map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="px-3 py-10 text-center text-ink-muted text-xs">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-10 text-center text-ink-muted text-xs">
                  <Bank size={24} className="mx-auto mb-2 opacity-40" />
                  No payroll rows for this month.
                </td>
              </tr>
            ) : (
              <>
                {rows.map((r) => (
                  <tr key={r.employee_id} className="border-b border-canvas-line/50" data-testid={`pfesi-row-${r.employee_code}`}>
                    <td className="px-3 py-2 font-mono">{r.employee_code}</td>
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2 text-xs text-ink-muted">{r.branch_name}</td>
                    <td className="px-3 py-2 font-mono">{inr(r.gross)}</td>
                    <td className="px-3 py-2 font-mono">{r.pf_applicable === "YES" ? inr(r.epf_ee) : "—"}</td>
                    <td className="px-3 py-2 font-mono">{r.pf_applicable === "YES" ? inr(r.eps_er) : "—"}</td>
                    <td className="px-3 py-2 font-mono">{r.pf_applicable === "YES" ? inr(r.epf_er) : "—"}</td>
                    <td className="px-3 py-2 font-mono">{r.esi_wages ? inr(r.esi_ee) : "—"}</td>
                    <td className="px-3 py-2 font-mono">{r.esi_wages ? inr(r.esi_er) : "—"}</td>
                    <td className="px-3 py-2 font-mono font-semibold">{inr(r.total_ee + r.total_er)}</td>
                  </tr>
                ))}
                <tr className="bg-canvas-panel font-semibold" data-testid="pfesi-totals-row">
                  <td className="px-3 py-2 font-mono text-[10px] uppercase" colSpan={3}>Totals</td>
                  <td className="px-3 py-2 font-mono">{inr(t.gross)}</td>
                  <td className="px-3 py-2 font-mono">{inr(t.epf_ee)}</td>
                  <td className="px-3 py-2 font-mono">{inr(t.eps_er)}</td>
                  <td className="px-3 py-2 font-mono">{inr(t.epf_er)}</td>
                  <td className="px-3 py-2 font-mono">{inr(t.esi_ee)}</td>
                  <td className="px-3 py-2 font-mono">{inr(t.esi_er)}</td>
                  <td className="px-3 py-2 font-mono">{inr((t.epf_ee || 0) + (t.eps_er || 0) + (t.epf_er || 0) + (t.esi_ee || 0) + (t.esi_er || 0))}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
